import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';

import { Key } from 'ts-key-enum';

const focusableSelector = `a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]),
      input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])`;

@Directive({
  selector: '[shrdUicFocusTrap]',
  exportAs: 'shrdUicFocusTrap'
})
export class FocusTrapDirective implements OnInit, AfterViewInit, OnDestroy {
  controls: string;
  firstFocusableElement: HTMLElement;
  lastFocusableElement: HTMLElement;
  private mutationObserver: MutationObserver = null;

  @Input() autoUpdate = false;

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  @HostListener('focus')
  private onFocus(): void {
    this.elementRef.nativeElement.style.outline = 'none';
  }

  @HostListener('keydown', ['$event', '$event.target'])
  private handleKeyboardEvent(event: KeyboardEvent, target: HTMLElement): void {
    if (event.key !== Key.Tab) {
      return;
    }

    if (event.shiftKey) {
      if (target === this.firstFocusableElement) {
        this.lastFocusableElement.focus();
        event.preventDefault();
      }
    } else {
      if (target === this.lastFocusableElement) {
        this.firstFocusableElement.focus();
        event.preventDefault();
      }
    }
  }

  ngOnInit(): void {
    this.elementRef.nativeElement.setAttribute('tabindex', '-1');
    this.setFocus();
  }

  ngAfterViewInit(): void {
    this.updateFocusableElements();
    if (this.autoUpdate) {
      this.observeFocusableElementsChange();
    }
  }

  ngOnDestroy(): void {
    this.unobserveFocusableElementsChange();
  }

  private setFocus(): void {
    this.elementRef.nativeElement.focus({
      preventScroll: true
    });
  }

  private updateFocusableElements(): void {
    const focusableElements: NodeListOf<HTMLElement> =
      this.elementRef.nativeElement.querySelectorAll(focusableSelector);
    if (!focusableElements.length) {
      return;
    }
    this.firstFocusableElement = focusableElements[0];
    this.lastFocusableElement = focusableElements[focusableElements.length - 1];
  }

  private observeFocusableElementsChange(): void {
    this.mutationObserver = new MutationObserver(records => {
      for (const { addedNodes, removedNodes, attributeName } of records) {
        // update if focusable elements added / removed
        const shouldUpdateFocusableElements = [...Array.from(addedNodes), ...Array.from(removedNodes)].some(
          node => node.nodeType === Node.ELEMENT_NODE && !!(node as Element).querySelector(focusableSelector)
        );
        // update if disabled or tabindex change
        if (attributeName || shouldUpdateFocusableElements) {
          this.updateFocusableElements();
          break;
        }
      }
    });
    this.mutationObserver.observe(this.elementRef.nativeElement, {
      attributeFilter: ['tabindex', 'disabled'],
      childList: true,
      subtree: true
    });
  }

  private unobserveFocusableElementsChange(): void {
    this.mutationObserver?.disconnect();
  }
}
